if (!customElements.get("sticky-atc-bar")) {
  customElements.define(
    "sticky-atc-bar",
    class StickyAtcBar extends HTMLElement {
      constructor() {
        super();
        this.currentVariant = null;
        this.isVisible = false;
        this.variantChangeUnsubscriber = null;
        this.sectionId = this.dataset.sectionId || null;

        // Check if mobile should always be visible
        this.mobileAlwaysVisible = this.dataset.mobileAlwaysVisible === "true";
      }

      connectedCallback() {
        this.setupIntersectionObserver();
        this.bindEvents();
        this.getCurrentVariant();

        // If mobile should always be visible, show immediately on mobile
        if (this.mobileAlwaysVisible) {
          this.checkMobileVisibility();
        }
      }

      setupIntersectionObserver() {
        // Skip intersection observer if mobile should always be visible
        if (
          this.mobileAlwaysVisible &&
          window.matchMedia("(max-width: 767px)").matches
        ) {
          return;
        }

        const triggerElement = document.querySelector('[id^="MainProduct"]');
        if (!triggerElement) return;

        this.intersectionObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              // On mobile with mobileAlwaysVisible, ignore intersection changes
              if (
                this.mobileAlwaysVisible &&
                window.matchMedia("(max-width: 767px)").matches
              ) {
                return;
              }

              const shouldShow = !entry.isIntersecting;
              this.toggleVisibility(shouldShow);
            });
          },
          { threshold: 0, rootMargin: "0px 0px -100px 0px" }
        );

        this.intersectionObserver.observe(triggerElement);
      }

      toggleVisibility(show) {
        if (show === this.isVisible) return;
        this.isVisible = show;
        this.classList.toggle("visible", show);
      }

      checkMobileVisibility() {
        const mql = window.matchMedia("(max-width: 767px)");

        const handleMobileChange = () => {
          if (this.mobileAlwaysVisible && mql.matches) {
            // On mobile with mobileAlwaysVisible enabled, always show
            this.toggleVisibility(true);
          } else if (!mql.matches) {
            // On desktop, use normal intersection observer behavior
            // Reset visibility based on current scroll position
            const triggerElement = document.querySelector(
              '[id^="MainProduct"]'
            );
            if (triggerElement) {
              const rect = triggerElement.getBoundingClientRect();
              const shouldShow = rect.bottom < 100; // Similar to intersection observer logic
              this.toggleVisibility(shouldShow);
            }
          }
        };

        // Initial check
        handleMobileChange();

        // Listen for screen size changes
        mql.addEventListener("change", handleMobileChange);
      }

      bindEvents() {
        // Use the theme's pub/sub system (more reliable than variant:changed event)
        if (window.FoxTheme && FoxTheme.pubsub) {
          console.log("Sticky ATC: Using FoxTheme pubsub system");

          const unsubscriber = FoxTheme.pubsub.subscribe(
            FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
            ({ data: { variant } }) => {
              console.log("Sticky ATC: FoxTheme variant change:", variant);

              if (variant) {
                this.currentVariant = variant;
                this.updateButton();
                this.updateVariantTitle(variant);
                this.updatePrice(variant);
                this.updateQuantitySelector(variant);
              }
            }
          );

          // Store unsubscriber for cleanup
          this.variantChangeUnsubscriber = unsubscriber;
        } else {
          console.log(
            "Sticky ATC: FoxTheme pubsub not available, falling back to form monitoring"
          );

          // Fallback to form monitoring if pub/sub isn't available
          const mainForm = document.querySelector('form[action*="/cart/add"]');
          const variantInput = mainForm?.querySelector('input[name="id"]');

          if (variantInput) {
            let lastVariantId = variantInput.value;

            const checkForChanges = () => {
              const currentVariantId = variantInput.value;
              if (currentVariantId !== lastVariantId) {
                console.log(
                  "Sticky ATC: Variant changed via fallback:",
                  currentVariantId
                );
                lastVariantId = currentVariantId;

                if (currentVariantId) {
                  // For fallback, we need to get variant data from the main product form
                  this.getVariantDataAndUpdate(parseInt(currentVariantId));
                }
              }
            };

            setInterval(checkForChanges, 100);
          }
        }

        // Handle form submission - this is the key change
        const button = this.querySelector(".sticky-atc-bar__button");
        if (button) {
          button.addEventListener("click", this.handleAddToCart.bind(this));
        }

        // Set up quantity synchronization
        this.setupQuantitySync();
      }

      async getVariantDataAndUpdate(variantId) {
        // Try to get variant data from the main product price element or product JSON
        const productJson = document.querySelector(
          '[type="application/json"][data-product-json]'
        );

        if (productJson) {
          try {
            const product = JSON.parse(productJson.textContent);
            const variant = product.variants.find((v) => v.id === variantId);

            if (variant) {
              this.currentVariant = variant;
              this.updateButton();
              this.updatePrice(variant);
              this.updateQuantitySelector(variant);
            }
          } catch (error) {
            console.error("Error parsing product JSON:", error);
          }
        }
      }

      getCurrentVariant() {
        // Get current variant from main form
        const mainForm = document.querySelector('form[action*="/cart/add"]');
        const variantInput = mainForm?.querySelector('input[name="id"]');

        if (variantInput?.value) {
          this.getVariantDataAndUpdate(parseInt(variantInput.value));
        }
      }

      updateButton() {
        const button = this.querySelector(".sticky-atc-bar__button");
        const buttonText = button?.querySelector("span");

        if (!button) return;

        if (!this.currentVariant || !this.currentVariant.available) {
          button.setAttribute("disabled", "disabled");
          if (buttonText) {
            buttonText.textContent = "Unavailable";
          }
        } else {
          button.removeAttribute("disabled");
          if (buttonText) {
            buttonText.textContent = "Add to cart";
          }
        }
      }

      updateVariantTitle(variant) {
        const variantTitleElement = this.querySelector(
          ".sticky-atc-bar__variant-title"
        );
        if (variantTitleElement && variant?.title) {
          variantTitleElement.textContent = variant.title;
          console.log("Sticky ATC: Updated variant title to:", variant.title);
        }
      }

      updateQuantitySelector(variant) {
        const quantityInput = this.querySelector("quantity-input");
        const quantityInputField = this.querySelector('input[name="quantity"]');

        if (!quantityInput || !quantityInputField || !variant) return;

        // Update quantity input attributes based on new variant
        quantityInputField.setAttribute("data-quantity-variant-id", variant.id);
        quantityInputField.setAttribute(
          "step",
          variant.quantity_rule?.increment || 1
        );
        quantityInputField.setAttribute(
          "data-min",
          variant.quantity_rule?.min || 1
        );
        quantityInputField.setAttribute("min", variant.quantity_rule?.min || 1);
        quantityInputField.value = variant.quantity_rule?.min || 1;

        if (variant.quantity_rule?.max) {
          quantityInputField.setAttribute(
            "data-max",
            variant.quantity_rule.max
          );
          quantityInputField.setAttribute("max", variant.quantity_rule.max);
        } else {
          quantityInputField.removeAttribute("data-max");
          quantityInputField.removeAttribute("max");
        }

        // Update cart quantity if available
        const cartQty = this.getCartQuantityForVariant(variant.id);
        quantityInputField.setAttribute("data-cart-quantity", cartQty);

        // Trigger quantity validation
        if (quantityInput.validateQtyRules) {
          quantityInput.validateQtyRules();
        }

        // Show/hide quantity selector based on availability
        const quantityContainer = this.querySelector(".product-form__quantity");
        if (quantityContainer) {
          quantityContainer.classList.toggle("hidden", !variant.available);
        }

        console.log(
          "Sticky ATC: Updated quantity selector for variant:",
          variant.id
        );
      }

      getCartQuantityForVariant(variantId) {
        // Try to get cart quantity from main product form or calculate from cart
        const mainQuantityInput = document.querySelector(
          `input[data-quantity-variant-id="${variantId}"]`
        );
        if (mainQuantityInput) {
          return (
            parseInt(mainQuantityInput.getAttribute("data-cart-quantity")) || 0
          );
        }
        return 0;
      }

      getSelectedQuantity() {
        const quantityInput = this.querySelector('input[name="quantity"]');
        return quantityInput ? parseInt(quantityInput.value) || 1 : 1;
      }

      setupQuantitySync() {
        // Wait for both elements to be available
        const waitForElements = () => {
          const stickyQuantityInput = this.querySelector(
            'input[name="quantity"]'
          );
          const mainQuantityInput = document.querySelector(
            'form[action*="/cart/add"] input[name="quantity"]'
          );

          if (!stickyQuantityInput || !mainQuantityInput) {
            console.log("Sticky ATC: Waiting for quantity inputs...");
            // Try again after a short delay
            setTimeout(waitForElements, 100);
            return;
          }

          console.log(
            "Sticky ATC: Found both quantity inputs, setting up sync"
          );

          // Sync from main form to sticky bar
          mainQuantityInput.addEventListener("change", (e) => {
            if (stickyQuantityInput.value !== e.target.value) {
              stickyQuantityInput.value = e.target.value;
              this.validateStickyQuantity();
              console.log("Synced main -> sticky:", e.target.value);
            }
          });

          mainQuantityInput.addEventListener("input", (e) => {
            if (stickyQuantityInput.value !== e.target.value) {
              stickyQuantityInput.value = e.target.value;
              this.validateStickyQuantity();
              console.log("Synced main -> sticky (input):", e.target.value);
            }
          });

          // Sync from sticky bar to main form
          stickyQuantityInput.addEventListener("change", (e) => {
            if (mainQuantityInput.value !== e.target.value) {
              mainQuantityInput.value = e.target.value;
              this.validateMainQuantity(mainQuantityInput);
              // Trigger change event on main form for any other listeners
              mainQuantityInput.dispatchEvent(
                new Event("change", { bubbles: true })
              );
              console.log("Synced sticky -> main:", e.target.value);
            }
          });

          stickyQuantityInput.addEventListener("input", (e) => {
            if (mainQuantityInput.value !== e.target.value) {
              mainQuantityInput.value = e.target.value;
              this.validateMainQuantity(mainQuantityInput);
              // Trigger input event on main form for any other listeners
              mainQuantityInput.dispatchEvent(
                new Event("input", { bubbles: true })
              );
              console.log("Synced sticky -> main (input):", e.target.value);
            }
          });

          // Set initial sync
          if (
            mainQuantityInput.value &&
            stickyQuantityInput.value !== mainQuantityInput.value
          ) {
            stickyQuantityInput.value = mainQuantityInput.value;
            console.log("Initial sync set to:", mainQuantityInput.value);
          }

          console.log(
            "Sticky ATC: Quantity synchronization set up successfully"
          );
        };

        // Start the process
        waitForElements();
      }

      validateStickyQuantity() {
        const quantityInput = this.querySelector("quantity-input");
        if (quantityInput && quantityInput.validateQtyRules) {
          quantityInput.validateQtyRules();
        }
      }

      validateMainQuantity(mainQuantityInput) {
        // Find the main quantity-input element and trigger its validation
        const mainQuantityElement = mainQuantityInput.closest("quantity-input");
        if (mainQuantityElement && mainQuantityElement.validateQtyRules) {
          mainQuantityElement.validateQtyRules();
        }
      }

      updatePrice(variant) {
        if (!variant) return;

        // Method 1: Use Section Rendering API if section ID is available
        if (this.sectionId) {
          this.updatePriceViaSectionRendering(variant);
        } else {
          // Method 2: Update price manually using variant data
          this.updatePriceDirectly(variant);
        }
      }

      async updatePriceViaSectionRendering(variant) {
        try {
          const searchParams = new URLSearchParams(window.location.search);
          searchParams.set("variant", variant.id);
          searchParams.set("sections", this.sectionId);

          const fetchUrl = `${window.location.pathname}?${searchParams.toString()}`;

          const response = await fetch(fetchUrl);
          const data = await response.json();

          if (data[this.sectionId]) {
            // Parse the returned HTML and extract the price element
            const parser = new DOMParser();
            const doc = parser.parseFromString(
              data[this.sectionId],
              "text/html"
            );
            const newPriceElement = doc.querySelector('[id^="StickyPrice"]');

            if (newPriceElement) {
              const currentPriceElement = this.querySelector(
                '[id^="StickyPrice"]'
              );
              if (currentPriceElement) {
                currentPriceElement.innerHTML = newPriceElement.innerHTML;
                console.log("Sticky ATC: Updated price via section rendering");
              }
            }
          }
        } catch (error) {
          console.error("Failed to update price via section rendering:", error);
          // Fallback to direct price update
          this.updatePriceDirectly(variant);
        }
      }

      updatePriceDirectly(variant) {
        const priceElement = this.querySelector('[id^="StickyPrice"]');
        if (!priceElement || !variant) return;

        try {
          // Format price using Shopify's money format
          const formatMoney = (cents) => {
            const money = (cents / 100).toFixed(2);
            // Basic money formatting - you might want to use your theme's money format
            return `$${money}`;
          };

          // Update regular price
          const regularPriceEl = priceElement.querySelector(
            ".price-item--regular"
          );
          const salePriceEl = priceElement.querySelector(".price-item--sale");

          if (
            variant.compare_at_price &&
            variant.compare_at_price > variant.price
          ) {
            // Variant is on sale
            if (regularPriceEl) {
              regularPriceEl.textContent = formatMoney(
                variant.compare_at_price
              );
            }
            if (salePriceEl) {
              salePriceEl.textContent = formatMoney(variant.price);
            }
            priceElement.classList.add("price--on-sale");
          } else {
            // Regular price
            if (regularPriceEl) {
              regularPriceEl.textContent = formatMoney(variant.price);
            }
            if (salePriceEl) {
              salePriceEl.style.display = "none";
            }
            priceElement.classList.remove("price--on-sale");
          }

          console.log(
            "Sticky ATC: Updated price directly to:",
            formatMoney(variant.price)
          );
        } catch (error) {
          console.error("Error updating price directly:", error);
        }
      }

      // Proper integration with theme's cart system
      async handleAddToCart() {
        if (!this.currentVariant?.id) return;

        const button = this.querySelector(".sticky-atc-bar__button");
        const quantity = this.getSelectedQuantity();

        button.setAttribute("disabled", "disabled");
        button.classList.add("btn--loading");

        try {
          // Step 1: Collect sections to bundle (same as ProductForm)
          let sectionsToBundle = [];
          document.documentElement.dispatchEvent(
            new CustomEvent("cart:grouped-sections", {
              bubbles: true,
              detail: { sections: sectionsToBundle },
            })
          );

          // Step 2: Prepare form data (same as ProductForm)
          const formData = new FormData();
          formData.append("id", this.currentVariant.id);
          formData.append("quantity", quantity);
          formData.append("sections", sectionsToBundle);
          formData.append("sections_url", window.location.pathname);

          // Step 3: Use theme's fetch config (same as ProductForm)
          const config = FoxTheme.utils.fetchConfig("javascript");
          config.headers["X-Requested-With"] = "XMLHttpRequest";
          delete config.headers["Content-Type"];
          config.body = formData;

          // Step 4: Make the same fetch call as ProductForm
          const response = await fetch(
            `${FoxTheme.routes.cart_add_url}`,
            config
          );
          const parsedState = await response.json();

          if (parsedState.status) {
            // Handle error same as ProductForm
            this.handleCartError(parsedState);
            return;
          }

          // Step 5: Get cart JSON and update state (same as ProductForm)
          const cartJson = await fetch(`${FoxTheme.routes.cart_url}`, {
            ...FoxTheme.utils.fetchConfig(),
          }).then((r) => r.json());

          cartJson["sections"] = parsedState["sections"];

          // Step 6: Update cart state via pub/sub (same as ProductForm)
          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
            cart: cartJson,
          });

          // Step 7: Dispatch product added event (same as ProductForm)
          document.dispatchEvent(
            new CustomEvent("product-ajax:added", {
              detail: {
                product: parsedState,
              },
            })
          );

          // Step 8: Show cart drawer (same as ProductForm)
          this.showCartDrawer(button);
        } catch (error) {
          console.error("Error adding to cart:", error);
        } finally {
          button.removeAttribute("disabled");
          button.classList.remove("btn--loading");
        }
      }

      handleCartError(parsedState) {
        // Same error handling as ProductForm
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartError, {
          source: "sticky-atc-bar",
          productVariantId: this.currentVariant.id,
          errors: parsedState.errors || parsedState.description,
          message: parsedState.message,
        });

        document.dispatchEvent(
          new CustomEvent("product-ajax:error", {
            detail: {
              errorMessage: parsedState.description,
            },
          })
        );

        console.error("Cart error:", parsedState.description);
      }

      showCartDrawer(lastSubmittedElement) {
        // Same cart drawer logic as ProductForm
        const cartDrawerElement = document.querySelector("cart-drawer");
        if (!cartDrawerElement) return;

        const quickViewModal = this.closest("quick-view-modal");
        if (quickViewModal) {
          if (!cartDrawerElement.open) {
            document.body.addEventListener(
              quickViewModal.events.handleAfterHide,
              () => {
                setTimeout(() => {
                  cartDrawerElement.show(lastSubmittedElement);
                });
              },
              { once: true }
            );
          }
          quickViewModal.hide(true);
        } else {
          cartDrawerElement.show(lastSubmittedElement);
        }
      }

      disconnectedCallback() {
        if (this.intersectionObserver) {
          this.intersectionObserver.disconnect();
        }

        // Clean up pub/sub subscription
        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
        }
      }
    }
  );
}
